/* Vellucio — Gift Builder
   Step-by-step gift customisation.
   Holds all state itself and only touches Shopify at the final submit. */

(function () {
  if (customElements.get('gift-builder')) return;

  var STORE_PREFIX = 'vellucio-gift-';

  /* One unit for both the letter and the signature.

     cqw is 1% of the stage's width, so a size written in it is still a
     proportion after it has been set: the browser re-resolves it every
     time the stage changes width, and the type tracks the picture with
     no measuring, no resize listener and nothing to fall out of date.
     Where container queries are missing we fall back to pixels worked
     out from the stage's width, which is the old behaviour. */
  var HAS_CQ = typeof CSS !== 'undefined' && CSS.supports
    && CSS.supports('font-size', '1cqw');

  function relativeUnit(stage) {
    if (HAS_CQ) return { name: 'cqw', px: false, width: 100 };
    return { name: 'px', px: true, width: (stage && stage.clientWidth) || 600 };
  }

  class GiftBuilder extends HTMLElement {
    connectedCallback() {
      this.min = parseInt(this.dataset.min, 10) || 3;
      this.max = parseInt(this.dataset.max, 10) || 3;
      /* Two numbers, not one: the making and the travelling are separate
         promises, and only the first is ours to keep.

         They are also counted differently. Making happens whenever we are
         at the bench, weekends included, so those are plain days. Travel
         does not: a parcel handed over on Friday does not move again until
         Monday, so transit is counted in working days and dispatch has to
         land on one. Counting both as calendar days produced schedules
         that had us posting things on a Sunday. */
      this.makeDays = parseInt(this.dataset.makeDays, 10) || 2;
      this.transitDays = parseInt(this.dataset.transitDays, 10) || 4;
      this.shipDays = (this.dataset.shipDays || '1,2,3,4,5')
        .split(',')
        .map(function (n) { return parseInt(n, 10); })
        .filter(function (n) { return !isNaN(n); });
      this.maxSeconds = parseInt(this.dataset.maxSeconds, 10) || 90;
      /* Where the Worker hands out one-time Cloudflare Stream upload
         links, and the object URL of the file they picked — played
         locally so the preview appears before the upload finishes. */
      this.uploadUrl = this.dataset.uploadUrl || '';
      this.photoUploadUrl = this.dataset.photoUploadUrl || '';
      this.videoPreview = '';
      this.photoPreview = '';
      /* An upload in flight. Leaving the step while one is running
         means the order can be placed without the file. */
      this.busy = { photo: false, video: false };
      this.storageKey = STORE_PREFIX + (this.dataset.product || 'default');

      this.steps = Array.from(this.querySelectorAll('[data-step]'));
      this.cards = Array.from(this.querySelectorAll('[data-choc]'));
      this.optionSteps = this.steps.filter(function (step) {
        return step.dataset.key.indexOf('option-') === 0;
      });
      this.index = 0;

      this.variants = [];
      var variantData = this.querySelector('[data-variants]');
      if (variantData) {
        try { this.variants = JSON.parse(variantData.textContent); } catch (e) { /* ignore */ }
      }

      this.state = {
        name: '', occasion: '',
        options: this.optionSteps.map(function () { return ''; }),
        variant: '',
        counts: this.cards.map(function () { return 0; }),
        message: '', sender: '',
        photo: '', video: '', arrival: '', reveal: '', recipientEmail: ''
      };

      this.imageSets = [];
      var setData = this.querySelector('[data-image-sets]');
      if (setData) {
        try { this.imageSets = JSON.parse(setData.textContent); } catch (e) { /* ignore */ }
      }
      this.fallback = this.dataset.fallback ? [this.dataset.fallback] : [];

      this.restore();
      this.applyOptionDefaults();
      this.mountStage();
      this.setDateBounds();
      this.bind();
      this.wireUploads();
      this.hideThemeButton();
      this.wireStickyBar();
      this.render();

      /* A date picked last week can be too soon by the time they come
         back. Everything else they chose is still here, so this drops
         them on the one step that needs redoing rather than restarting
         them. */
      if (this.expireDate()) {
        this.render();
        this.showError('That date is now too soon for us to make and send it. '
          + 'Please choose another — everything else you chose is still here.');
      }

      this.verifyMedia();
    }

    /* A saved gift can outlive its uploads: a photo nobody bought is
       swept after three days, a video after a month. The address in
       storage then points at nothing, and the first the customer would
       hear of it is an empty frame at the review. Check instead, and
       put them back on the step that needs redoing. */
    verifyMedia() {
      var self = this;

      if (this.state.photo) {
        var probe = new Image();
        probe.onerror = function () { self.mediaGone('photo'); };
        probe.src = this.state.photo;
      }

      /* Stream's own thumbnail: present for any video it still holds,
         404 once the video has gone. */
      var code = this.dataset.streamCustomer;
      if (this.state.video && code) {
        var thumb = new Image();
        thumb.onerror = function () { self.mediaGone('video'); };
        thumb.src = 'https://' + code + '.cloudflarestream.com/' +
          this.state.video + '/thumbnails/thumbnail.jpg';
      }
    }

    mediaGone(kind) {
      /* Cleared here rather than through dropPhoto/dropVideo — there is
         nothing left on Cloudflare to ask it to delete. */
      this.state[kind] = '';
      this.busy[kind] = false;
      this.setUploadState(kind, 'idle');
      this.noteFile(kind, '');

      if (kind === 'photo') {
        this.photoPreview = '';
        this.showUserPhoto('');
      } else {
        this.videoPreview = '';
        if (this.stepKey() === 'video') this.showUserVideo('');
      }

      this.save();

      /* Only ever backwards. With both gone, the photo step is the
         earlier of the two and wins whichever check lands first. */
      var at = -1;
      this.steps.forEach(function (step, i) {
        if (step.dataset.key === kind) at = i;
      });
      if (at > -1 && at <= this.index) {
        this.index = at;
        this.render();
      }

      this.showError(kind === 'photo'
        ? 'Your photograph is no longer saved \u2014 uploads sit with us for '
          + 'a few days, and this one has been cleared. Please add it again.'
        : 'Your video is no longer saved \u2014 uploads sit with us for a '
          + 'while, and this one has been cleared. Please add it again.');
    }

    expireDate() {
      if (!this.state.arrival) return false;

      var earliest = this.earliestArrival();
      var chosen = new Date(this.state.arrival + 'T12:00:00');
      if (isNaN(chosen.getTime()) || chosen >= earliest) return false;

      this.state.arrival = '';
      var input = this.querySelector('[data-field="arrival"]');
      if (input) input.value = '';

      var at = -1;
      this.steps.forEach(function (step, i) {
        if (step.dataset.key === 'arrival') at = i;
      });
      if (at > -1) this.index = at;

      this.save();
      this.showSchedule();
      return true;
    }

    /* ---------- persistence ---------- */

    restore() {
      try {
        var raw = window.localStorage.getItem(this.storageKey);
        if (!raw) return;
        var saved = JSON.parse(raw);
        var self = this;
        Object.keys(this.state).forEach(function (k) {
          if (saved[k] !== undefined && saved[k] !== null) self.state[k] = saved[k];
        });
        if (!Array.isArray(this.state.counts) || this.state.counts.length !== this.cards.length) {
          this.state.counts = this.cards.map(function () { return 0; });
        }
        if (!Array.isArray(this.state.options) || this.state.options.length !== this.optionSteps.length) {
          this.state.options = this.optionSteps.map(function () { return ''; });
        }
        /* A resumed order has no record of the order things were picked
           in; ascending is the honest guess. */
        this.order = [];
        this.state.counts.forEach(function (count, i) {
          if (count > 0) this.order.push(i);
        }, this);

        this.resolveVariant();
        this.writeToFields();

        /* Put them back on the step they were on, not at the beginning. */
        var savedIndex = parseInt(saved._index, 10);
        if (!isNaN(savedIndex) && savedIndex >= 0 && savedIndex < this.steps.length) {
          this.index = savedIndex;
        }

        this.flashSaved('We’ve brought you back to where you left off.', true);
      } catch (e) { /* storage unavailable — carry on */ }
    }

    /* The stage shows the first box from the moment they arrive, so the
       radio should agree with it rather than leaving them wondering
       whether they have chosen anything. */
    applyOptionDefaults() {
      var self = this;
      this.optionSteps.forEach(function (step, i) {
        if (self.state.options[i]) return;
        var first = step.querySelector('[data-option]:not([disabled])');
        if (!first) return;
        self.state.options[i] = first.value;
        first.checked = true;
      });
      this.resolveVariant();
    }

    save() {
      try {
        var payload = JSON.parse(JSON.stringify(this.state));
        payload._index = this.index;
        window.localStorage.setItem(this.storageKey, JSON.stringify(payload));
        this.flashSaved('Saved — you can come back to this later.');
      } catch (e) { /* ignore */ }
    }

    /* A transient note while they work; a sticky one with a way out when
       we have restored a previous session, since someone starting a new
       gift needs to be able to say so. */
    flashSaved(text, sticky) {
      var el = this.querySelector('[data-saved]');
      if (!el) return;

      var label = el.querySelector('[data-saved-text]');
      if (label) label.textContent = text;
      el.hidden = false;

      var reset = el.querySelector('[data-reset]');
      if (reset && sticky) reset.hidden = false;

      clearTimeout(this._savedTimer);
      if (sticky) return;

      this._savedTimer = setTimeout(function () {
        if (reset && !reset.hidden) return;
        el.hidden = true;
      }, 2600);
    }

    startAgain() {
      try { window.localStorage.removeItem(this.storageKey); } catch (e) { /* ignore */ }
      window.location.reload();
    }

    writeToFields() {
      var self = this;

      this.querySelectorAll('[data-option]').forEach(function (el) {
        var i = parseInt(el.dataset.option, 10);
        el.checked = self.state.options[i] === el.value;
      });

      this.querySelectorAll('[data-field]').forEach(function (el) {
        var key = el.dataset.field;
        var value = self.state[key];
        if (el.type === 'radio') {
          el.checked = (el.value === value);
        } else if (value) {
          el.value = value;
        }
      });
      if (this.state.photo) { this.noteFile('photo', this.state.photo); this.setUploadState('photo', 'done'); }
      if (this.state.video) { this.noteFile('video', this.state.video); this.setUploadState('video', 'done'); }
    }

    /* ---------- setup ---------- */

    /* ---------- working days ---------- */

    worksOn(date) { return this.shipDays.indexOf(date.getDay()) > -1; }

    backToWorkDay(date) {
      var out = new Date(date.getTime());
      while (!this.worksOn(out)) out.setDate(out.getDate() - 1);
      return out;
    }

    onToWorkDay(date) {
      var out = new Date(date.getTime());
      while (!this.worksOn(out)) out.setDate(out.getDate() + 1);
      return out;
    }

    minusWorkDays(date, count) {
      var out = new Date(date.getTime());
      for (var i = 0; i < count; i++) {
        do { out.setDate(out.getDate() - 1); } while (!this.worksOn(out));
      }
      return out;
    }

    plusWorkDays(date, count) {
      var out = new Date(date.getTime());
      for (var i = 0; i < count; i++) {
        do { out.setDate(out.getDate() + 1); } while (!this.worksOn(out));
      }
      return out;
    }

    /* Today at the bench, not today where the customer is standing.

       The making calendar is ours, and Turkiye runs seven to ten hours
       ahead of the Americas — so from late afternoon in New York it is
       already tomorrow here. Left on the browser's clock, an evening
       shopper in the States was offered a date a full day earlier than we
       could reach, computed from a day we had already finished. */
    todayHere() {
      var stamp;
      try {
        stamp = new Intl.DateTimeFormat('en-CA', {
          timeZone: this.dataset.timezone || 'Europe/Istanbul',
          year: 'numeric', month: '2-digit', day: '2-digit'
        }).format(new Date());
      } catch (e) { stamp = null; }

      /* No Intl, or an unknown zone: the browser's own date is a poorer
         answer but a working one. */
      if (!stamp || stamp.length < 10) {
        var local = new Date();
        local.setHours(0, 0, 0, 0);
        return local;
      }

      var parts = stamp.slice(0, 10).split('-');
      return new Date(parseInt(parts[0], 10),
                      parseInt(parts[1], 10) - 1,
                      parseInt(parts[2], 10));
    }

    /* The soonest date we could actually meet: make for two days from
       today, hand it over on the next day the courier collects, then four
       working days on the road. */
    earliestArrival() {
      var start = this.todayHere();
      start.setDate(start.getDate() + this.makeDays);
      return this.plusWorkDays(this.onToWorkDay(start), this.transitDays);
    }

    setDateBounds() {
      var input = this.querySelector('[data-field="arrival"]');
      if (!input) return;
      input.min = this.iso(this.earliestArrival());
      if (!this.state.arrival) input.value = '';
      this.showSchedule();
    }

    /* Counted back from the date they picked, not forward from today.

       A weekend date is not refused — couriers rarely deliver then, so we
       aim at the Friday before instead, which still satisfies "arrive by".
       The date they chose is kept so we can say what we did. */
    plan() {
      if (!this.state.arrival) return null;
      var asked = new Date(this.state.arrival + 'T12:00:00');
      if (isNaN(asked.getTime())) return null;

      var arrive = this.backToWorkDay(asked);
      var ship = this.minusWorkDays(arrive, this.transitDays);

      var make = new Date(ship);
      make.setDate(make.getDate() - this.makeDays);

      return { asked: asked, arrive: arrive, ship: ship, make: make,
               moved: this.iso(arrive) !== this.iso(asked) };
    }

    /* Built from the local parts, never from toISOString.

       toISOString converts to UTC first, so at UTC+3 a Date sitting at
       local midnight becomes the previous day the moment it is written
       out. Every date here was landing a day early — which is how a
       Thursday the courier could not possibly reach became selectable,
       and why the line beneath then read back a dispatch date already in
       the past. */
    iso(date) {
      var month = String(date.getMonth() + 1);
      var day = String(date.getDate());
      if (month.length < 2) month = '0' + month;
      if (day.length < 2) day = '0' + day;
      return date.getFullYear() + '-' + month + '-' + day;
    }

    showSchedule() {
      var line = this.querySelector('[data-schedule]');
      if (!line) return;
      var plan = this.plan();
      if (!plan) { line.hidden = true; line.textContent = ''; return; }

      var text = 'We\u2019ll begin making it on ' +
        this.prettyDate(this.iso(plan.make)) + ' and send it on ' +
        this.prettyDate(this.iso(plan.ship)) + '.';

      if (plan.moved) {
        text += ' Couriers rarely deliver at weekends, so we\u2019ve aimed for '
          + this.prettyDate(this.iso(plan.arrive)) + ' to be safe.';
      }

      line.textContent = text;
      line.hidden = false;
    }

    hideThemeButton() {
      var form = this.closest('form');
      if (!form) return;
      var button = form.querySelector('button[name="add"]');
      if (button) button.style.display = 'none';
    }

    /* The theme's sticky bar adds to cart directly, which would place an
       order with no name, no chocolates and no letter. Turn it into a way
       back to the flow instead. */
    /* The theme's sticky bar is a real add-to-cart button. Left alone it
       drops an empty box in the cart, so it has to be neutralised on
       three fronts: listeners bound to the button itself (replaced with
       a copy, which keeps the styling and loses the listeners), native
       submission (type and name stripped), and — the one that kept
       getting through — the theme's delegated listener further up the
       tree, which fires on any click that reaches it. So nothing that
       starts inside the bar is allowed to travel. */
    wireStickyBar() {
      var self = this;

      var goToBuilder = function () {
        self.scrollIntoView({ behavior: 'smooth', block: 'center' });
      };

      var claim = function () {
        var button = document.querySelector('.sticky-add-to-cart__button');
        if (!button || button.dataset.gbCta) return;

        var fresh = button.cloneNode(true);
        fresh.dataset.gbCta = '1';
        fresh.setAttribute('type', 'button');
        ['name', 'value', 'form', 'disabled', 'aria-disabled'].forEach(function (attr) {
          fresh.removeAttribute(attr);
        });
        if (button.parentNode) button.parentNode.replaceChild(fresh, button);

        var label = fresh.querySelector('.add-to-cart-text__content span') || fresh;
        label.textContent = 'Customize your gift';
      };

      claim();

      /* The bar is rendered late and rebuilt by the theme editor. */
      document.addEventListener('shopify:section:load', function () { window.setTimeout(claim, 0); });
      if ('MutationObserver' in window) {
        this.stickyWatcher = new MutationObserver(function () { claim(); });
        this.stickyWatcher.observe(document.body, { childList: true, subtree: true });
      }

      /* Capture on window, which runs before anything the theme put on
         document, and stop the event dead rather than merely cancelling
         its default — the theme adds to the cart from a listener, not
         from the form. */
      var swallow = function (event) {
        var node = event.target;
        if (!node || !node.closest) return;
        if (!node.closest('.sticky-add-to-cart__button')) return;

        event.preventDefault();
        event.stopPropagation();
        if (event.stopImmediatePropagation) event.stopImmediatePropagation();

        if (event.type === 'click') goToBuilder();
      };

      ['pointerdown', 'pointerup', 'mousedown', 'mouseup', 'touchstart',
       'touchend', 'click', 'submit'].forEach(function (type) {
        window.addEventListener(type, swallow, true);
        document.addEventListener(type, swallow, true);
      });
    }

    bind() {
      var self = this;

      this.addEventListener('input', function (e) { self.onInput(e); });
      this.addEventListener('change', function (e) { self.onInput(e); });
      this.addEventListener('click', function (e) { self.onClick(e); });
      this.addEventListener('keydown', function (e) {
        if ((e.key === 'Enter' || e.key === ' ') && e.target.closest('[data-select]')) {
          e.preventDefault();
          self.onClick(e);
        }
      });
    }

    onInput(event) {
      var optionInput = event.target.closest('[data-option]');
      if (optionInput) {
        if (!optionInput.checked) return;
        var index = parseInt(optionInput.dataset.option, 10);
        this.state.options[index] = optionInput.value;
        this.resolveVariant();
        this.updateStage();
        this.clearError();
        this.save();
        return;
      }

      var field = event.target.closest('[data-field]');
      if (!field) return;
      var key = field.dataset.field;

      if (field.type === 'radio') {
        if (!field.checked) return;
        this.state[key] = field.value;
      } else {
        this.state[key] = field.value;
      }

      if (key === 'message') {
        var chars = this.querySelector('[data-chars]');
        if (chars) chars.textContent = String(field.value.length);
        this.showLetter(field.value);
      }
      if (key === 'sender') this.showLetter(this.state.message);
      if (key === 'reveal') this.toggleRecipientEmail();
      if (key === 'arrival') this.showSchedule();

      this.clearError();
      this.save();
      this.updateNames();
    }

    onClick(event) {
      var self = this;

      /* The last-call dialog, before anything else — it sits over the
         flow and its two buttons are the only things that should act
         while it is open. */
      var goBack = event.target.closest('[data-ask-back]');
      if (goBack) {
        this.closeAsk();
        this.jumpTo(goBack.dataset.askBack || 'photo');
        return;
      }

      if (event.target.closest('[data-ask-on]')) {
        this._asked = true;
        this.closeAsk();
        this.go(1);
        return;
      }

      /* Opening the ingredients must not also pick the chocolate — this
         runs before the selection handlers and returns. */
      var reveal = event.target.closest('[data-inside-toggle]');
      if (reveal) {
        var host = reveal.closest('[data-choc]');
        if (host) {
          var opening = !host.classList.contains('is-inside');
          host.classList.toggle('is-inside', opening);
          reveal.setAttribute('aria-expanded', opening ? 'true' : 'false');
          reveal.textContent = opening ? 'Hide' : "What's inside";
        }
        return;
      }

      /* The panel lies over the picture, and the picture is what selects
         the chocolate. Reading the ingredients is not choosing it. */
      if (event.target.closest('[data-inside]')) return;

      var stepBtn = event.target.closest('[data-step-choc]');
      if (stepBtn) {
        var card = stepBtn.closest('[data-choc]');
        var i = this.cards.indexOf(card);
        var delta = parseInt(stepBtn.dataset.stepChoc, 10);
        if (delta > 0 && this.total() >= this.max) return;
        this.state.counts[i] = Math.max(0, this.state.counts[i] + delta);

        if (this.state.counts[i] === 0) this.drop(i);
        else if (delta > 0) this.touch(i);

        this.renderChocolates();
        this.save();

        /* None left means the pictures the step opens with; otherwise the
           most recent choice still in the box. */
        if (delta > 0) this.showChocolate(this.cards[i]);
        else this.showChocolate(this.latest());
        return;
      }

      var photo = event.target.closest('[data-select]');
      if (photo) {
        var pcard = photo.closest('[data-choc]');
        var j = this.cards.indexOf(pcard);
        this.showChocolate(pcard);
        if (this.state.counts[j] === 0 && this.total() < this.max) {
          this.state.counts[j] = 1;
          this.touch(j);
          this.renderChocolates();
          this.save();
        } else if (this.state.counts[j] > 0) {
          this.touch(j);
        }
        return;
      }

      var tpl = event.target.closest('[data-message]');
      if (tpl) {
        var area = this.querySelector('[data-field="message"]');
        if (!area) return;
        if (area.value.trim() && !window.confirm('Replace what you have written?')) return;
        area.value = tpl.dataset.message;

        /* Update state directly rather than relying on a synthetic event. */
        this.state.message = area.value;
        var chars = this.querySelector('[data-chars]');
        if (chars) chars.textContent = String(area.value.length);
        this.showLetter(area.value);
        this.clearError();
        this.save();

        this.querySelectorAll('[data-message]').forEach(function (b) { b.classList.remove('is-active'); });
        tpl.classList.add('is-active');
        area.focus();
        area.setSelectionRange(area.value.length, area.value.length);
        return;
      }

      if (event.target.closest('[data-crop]')) { this.toggleCrop(); return; }
      if (event.target.closest('[data-reset]')) { this.startAgain(); return; }
      if (event.target.closest('[data-next]')) { this.go(1); return; }
      if (event.target.closest('[data-back]')) { this.go(-1); return; }
      if (event.target.closest('[data-submit]')) { this.submit(); return; }
    }

    /* ---------- uploads ---------- */

    wireUploads() {
      var self = this;

      this.wirePhotoInput();
      this.wireVideoInput();
    }

    /* ---------- video: straight to Cloudflare Stream ----------
       The file never touches us. The Worker mints a one-time upload
       link, the browser posts the file to Cloudflare, and we keep only
       the id that comes back.

       The preview plays from their own device rather than from the
       uploaded copy, so it appears the instant they choose a file
       instead of after the upload — and the duration check, the
       rotation and the whole phone mock-up carry on unchanged. */

    /* ---------- photo: straight to Cloudflare Images ----------
       Same shape as the video. The Worker mints a one-time upload URL,
       the browser posts the file to Cloudflare, and we keep the
       delivery URL that comes back. The preview is drawn from the
       customer's own file so it appears immediately. */

    wirePhotoInput() {
      var self = this;
      var input = this.querySelector('[data-photo-input]');
      var button = this.querySelector('[data-open="photo"]');
      if (!input || !button) return;

      button.addEventListener('click', function () { input.click(); });

      input.addEventListener('change', function () {
        var file = input.files && input.files[0];
        if (file) self.uploadPhoto(file);
      });
    }

    /* Chosen, not yet framed. Opens the cropper when the library is
       there, and falls back to using the file as-is when it isn't, so a
       blocked CDN never stops someone finishing their gift. */
    uploadPhoto(file) {
      if (!/^image\//.test(file.type)) {
        this.noteFile('photo', '', 'That is not an image. Try a photo.');
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        this.noteFile('photo', '', 'That photo is over 10 MB. Try a smaller one.');
        return;
      }

      var dialog = this.querySelector('[data-cropper]');
      if (dialog && window.Cropper && typeof dialog.showModal === 'function') {
        this.openCropper(file);
        return;
      }

      this.usePhoto(file);
    }

    openCropper(file) {
      var self = this;
      var dialog = this.querySelector('[data-cropper]');
      var img = dialog.querySelector('[data-crop-img]');
      var url = URL.createObjectURL(file);

      function close() {
        try { dialog.close(); } catch (e) { /* already closed */ }
        if (self.cropper) { self.cropper.destroy(); self.cropper = null; }
        try { URL.revokeObjectURL(url); } catch (e) { /* ignore */ }
        img.removeAttribute('src');
        var input = self.querySelector('[data-photo-input]');
        if (input) input.value = '';
      }

      img.src = url;
      dialog.showModal();

      if (this.cropper) { this.cropper.destroy(); this.cropper = null; }
      this.cropper = new window.Cropper(img, {
        viewMode: 1,
        autoCropArea: 1,
        background: false,
        dragMode: 'move',
        toggleDragModeOnDblclick: false
      });

      /* Rebuilt on every open so the handlers close over this file. */
      var cancel = dialog.querySelector('[data-crop-cancel]');
      var rotate = dialog.querySelector('[data-crop-rotate]');
      var ok = dialog.querySelector('[data-crop-ok]');

      cancel.onclick = close;
      dialog.oncancel = function (e) { e.preventDefault(); close(); };
      rotate.onclick = function () { if (self.cropper) self.cropper.rotate(90); };

      ok.onclick = function () {
        if (!self.cropper) { close(); return; }
        var canvas = self.cropper.getCroppedCanvas({
          maxWidth: 3000,
          maxHeight: 3000,
          imageSmoothingQuality: 'high'
        });
        if (!canvas) { close(); return; }
        canvas.toBlob(function (blob) {
          close();
          if (!blob) return;
          var name = (file.name || 'photo').replace(/\.[^.]+$/, '') + '.jpg';
          self.usePhoto(new File([blob], name, { type: 'image/jpeg' }));
        }, 'image/jpeg', 0.92);
      };
    }

    usePhoto(file) {
      var self = this;

      if (this.photoPreview) {
        try { URL.revokeObjectURL(this.photoPreview); } catch (e) { /* ignore */ }
      }
      this.photoPreview = URL.createObjectURL(file);
      this.showUserPhoto(this.photoPreview);

      this.busy.photo = true;
      this.setUploadState('photo', 'busy');
      this.noteFile('photo', 'pending', 'Uploading\u2026');

      this.sendToImages(file);
    }

    sendToImages(file) {
      var self = this;

      if (!this.photoUploadUrl) {
        this.busy.photo = false;
        this.setUploadState('photo', 'idle');
        this.noteFile('photo', '', 'Uploads are not set up. Please let us know.');
        return;
      }

      fetch(this.photoUploadUrl, { method: 'POST' })
        .then(function (response) { return response.json(); })
        .then(function (data) {
          if (!data || !data.uploadURL || !data.url) throw new Error('no upload url');
          var body = new FormData();
          body.append('file', file);
          return fetch(data.uploadURL, { method: 'POST', body: body })
            .then(function (response) {
              if (!response.ok) throw new Error('upload rejected');
              return data.url;
            });
        })
        .then(function (url) {
          self.busy.photo = false;
          /* Swapped for a new one — the old upload is now rubbish. */
          if (self.state.photo && self.state.photo !== url) {
            self.dropRemotePhoto(self.state.photo);
          }
          self.state.photo = url;
          self.setUploadState('photo', 'done');
          self.noteFile('photo', url, 'Photo added');
          self.clearError();
          self.save();
        })
        .catch(function () {
          self.busy.photo = false;
          self.dropPhoto();
          self.noteFile('photo', '', 'That did not upload. Try again?');
          self.save();
        });
    }

    /* The id sits between the account hash and the variant:
       https://imagedelivery.net/<hash>/<id>/public */
    photoId(url) {
      var match = /imagedelivery\.net\/[^/]+\/([^/?#]+)/.exec(String(url || ''));
      return match ? match[1] : '';
    }

    /* A photo they replaced or removed is of no use to anyone, so it
       goes rather than sitting in storage we pay for. Fire and forget:
       the Worker refuses anything that belongs to a paid order. */
    dropRemotePhoto(url) {
      var id = this.photoId(url);
      if (!id || !this.photoUploadUrl) return;
      var endpoint = this.photoUploadUrl.replace(/\/photo-url$/, '/photo-drop');
      try {
        fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: id })
        }).catch(function () {});
      } catch (e) { /* ignore */ }
    }

    dropPhoto() {
      this.busy.photo = false;
      this.dropRemotePhoto(this.state.photo);
      this.state.photo = '';
      if (this.photoPreview) {
        try { URL.revokeObjectURL(this.photoPreview); } catch (e) { /* ignore */ }
      }
      this.photoPreview = '';
      this.setUploadState('photo', 'idle');
      var input = this.querySelector('[data-photo-input]');
      if (input) input.value = '';
      this.showUserPhoto('');
    }

    wireVideoInput() {
      var self = this;
      var input = this.querySelector('[data-video-input]');
      var button = this.querySelector('[data-open="video"]');
      if (!input || !button) return;

      button.addEventListener('click', function () { input.click(); });

      input.addEventListener('change', function () {
        var file = input.files && input.files[0];
        if (file) self.uploadVideo(file);
      });
    }

    uploadVideo(file) {
      var self = this;

      if (this.videoPreview) {
        try { URL.revokeObjectURL(this.videoPreview); } catch (e) { /* ignore */ }
      }
      this.videoPreview = URL.createObjectURL(file);

      this.busy.video = true;
      this.setUploadState('video', 'busy');
      this.noteFile('video', 'pending', 'Checking the length\u2026');

      /* Measured before anything is sent: a two-minute file should be
         refused on their machine, not after they have waited for it to
         climb up a phone connection. */
      this.checkDuration(this.videoPreview, function (ok, seconds) {
        if (!ok) {
          self.dropVideo();
          self.noteFile('video', '', 'That one is ' + self.clock(seconds) +
            ' long. Trim it to ' + self.maxSeconds + ' seconds or less and try again.');
          self.save();
          return;
        }

        if (self.stepKey() === 'video') self.showUserVideo(self.videoPreview);
        self.noteFile('video', 'pending',
          'Uploading' + (seconds ? ' \u00b7 ' + self.clock(seconds) : '') + '\u2026');

        self.sendToStream(file, seconds);
      });
    }

    sendToStream(file, seconds) {
      var self = this;

      if (!this.uploadUrl) {
        this.busy.video = false;
        this.setUploadState('video', 'idle');
        this.noteFile('video', '', 'Uploads are not set up. Please let us know.');
        return;
      }

      fetch(this.uploadUrl, { method: 'POST' })
        .then(function (response) { return response.json(); })
        .then(function (data) {
          if (!data || !data.uploadURL || !data.uid) throw new Error('no upload url');
          var body = new FormData();
          body.append('file', file);
          return fetch(data.uploadURL, { method: 'POST', body: body })
            .then(function (response) {
              if (!response.ok) throw new Error('upload rejected');
              return data.uid;
            });
        })
        .then(function (uid) {
          self.busy.video = false;
          self.state.video = uid;
          self.setUploadState('video', 'done');
          self.noteFile('video', uid,
            'Video added' + (seconds ? ' \u00b7 ' + self.clock(seconds) : ''));
          self.clearError();
          self.save();
        })
        .catch(function () {
          self.busy.video = false;
          self.dropVideo();
          self.noteFile('video', '', 'That did not upload. Try again?');
          self.save();
        });
    }

    /* Everything about the video forgotten — the id, the preview, and
       the input itself, so picking the same file again still fires a
       change event. */
    dropVideo() {
      this.busy.video = false;
      this.state.video = '';
      if (this.videoPreview) {
        try { URL.revokeObjectURL(this.videoPreview); } catch (e) { /* ignore */ }
      }
      this.videoPreview = '';
      this.setUploadState('video', 'idle');
      var input = this.querySelector('[data-video-input]');
      if (input) input.value = '';
      if (this.stepKey() === 'video') this.showUserVideo('');
    }

    acceptFile(kind, url, filename) {
      var self = this;

      if (kind === 'video') {
        this.setUploadState('video', 'busy');
        this.checkDuration(url, function (ok, seconds) {
          if (!ok) {
            self.state.video = '';
            self.setUploadState('video', 'idle');
            self.noteFile('video', '', 'That one is ' + self.clock(seconds) +
              ' long. Trim it to ' + self.maxSeconds + ' seconds or less and try again.');
            self.save();
            return;
          }
          self.state.video = url;
          self.setUploadState('video', 'done');
          if (self.stepKey() === 'video') self.showUserVideo(url);
          self.noteFile('video', url, (filename || 'Video') +
            (seconds ? ' · ' + self.clock(seconds) : ''));
          self.clearError();
          self.save();
        });
        return;
      }

      this.state.photo = url;
      this.setUploadState('photo', 'done');
      this.noteFile('photo', url, filename || 'Photo added');
      this.showUserPhoto(url);
      this.clearError();
      this.save();
    }

    clearFile(kind) {
      if (kind === 'video') {
        this.dropVideo();
        this.noteFile('video', '');
        this.save();
        return;
      }
      if (kind === 'photo') {
        this.dropPhoto();
        this.noteFile('photo', '');
        this.save();
        return;
      }
      if (!this.state[kind]) return;
      this.state[kind] = '';
      this.setUploadState(kind, 'idle');
      this.noteFile(kind, '');
      if (kind === 'video' && this.stepKey() === 'video') this.showUserVideo('');
      this.save();
    }

    /* The button collapses to a square with a tick once a file is in. */
    setUploadState(kind, mode) {
      var button = this.querySelector('[data-open="' + kind + '"]');
      if (!button) return;
      this.measureButtons();
      button.classList.toggle('is-busy', mode === 'busy');
      button.classList.toggle('is-done', mode === 'done');
      if (mode === 'done') {
        button.setAttribute('aria-label', 'Change the file you added');
      } else {
        button.removeAttribute('aria-label');
      }
    }

    /* Widths can only animate from a real number, and a hidden step
       measures as zero — so capture it the first time it is on screen. */
    measureButtons() {
      this.querySelectorAll('[data-open]').forEach(function (button) {
        if (button.dataset.width) return;
        var width = button.offsetWidth;
        if (!width) return;
        button.dataset.width = String(width);
        button.style.setProperty('--gb-w', width + 'px');
      });
    }

    checkDuration(url, done) {
      var self = this;
      var probe = document.createElement('video');
      probe.preload = 'metadata';
      var settled = false;

      probe.onloadedmetadata = function () {
        if (settled) return;
        settled = true;
        var seconds = probe.duration || 0;
        done(seconds <= self.maxSeconds + 5, seconds);
      };
      probe.onerror = function () {
        if (settled) return;
        settled = true;
        done(true, 0); // can't read it — don't block the customer
      };
      setTimeout(function () {
        if (settled) return;
        settled = true;
        done(true, 0);
      }, 8000);

      probe.src = url;
    }

    noteFile(kind, url, text) {
      var note = this.querySelector('[data-' + kind + '-note]');
      if (!note) return;
      if (!url && !text) { note.hidden = true; note.textContent = ''; return; }
      note.hidden = false;
      note.textContent = text || (kind === 'photo' ? 'Photo added.' : 'Video added.');
      note.classList.toggle('is-warn', !url);
    }

    /* "Wednesday, September 16". The T00:00:00 keeps it on the local day —
       without it the browser reads a bare date as UTC and anyone west of
       Greenwich sees yesterday, which on a delivery date is a real bug. */
    prettyDate(iso) {
      if (!iso) return '';
      try {
        return new Date(iso + 'T00:00:00').toLocaleDateString('en-US', {
          weekday: 'long', month: 'long', day: 'numeric'
        });
      } catch (e) {
        return iso;
      }
    }

    clock(seconds) {
      if (!seconds) return 'unknown length';
      var m = Math.floor(seconds / 60);
      var s = Math.round(seconds % 60);
      return m + ':' + (s < 10 ? '0' : '') + s;
    }

    /* ---------- navigation ---------- */

    go(direction) {
      if (direction > 0) {
        var here = this.steps[this.index].dataset.key;
        var problem = this.validate(here);
        if (problem) { this.showError(problem); return; }

        /* Leaving the last question before the review. Only one of the
           photo and the video is required, so the other can be skipped
           without ever being stopped — which is fine if it was a choice
           and a shame if it was an oversight. Ask once. */
        if (here === 'arrival' && !this._asked) {
          var missing = this.missingMedia();
          if (missing) { this.ask(missing); return; }
        }
      }
      var next = this.index + direction;
      if (next < 0 || next >= this.steps.length) return;
      this.index = next;
      this.clearError();
      this.render();
    }

    /* Which of the two they left out, or nothing if both are in. With
       neither we name the photo, since that is the one most people mean
       to add — but validation stops them long before here. */
    missingMedia() {
      if (!this.state.photo) return 'photo';
      if (!this.state.video) return 'video';
      return null;
    }

    ask(kind) {
      var box = this.querySelector('[data-ask]');
      if (!box) { this._asked = true; this.go(1); return; }

      var isPhoto = kind === 'photo';
      var title = box.querySelector('[data-ask-title]');
      var body = box.querySelector('[data-ask-body]');
      var back = box.querySelector('[data-ask-back]');

      if (title) {
        title.textContent = isPhoto
          ? 'You haven\u2019t added a photograph'
          : 'You haven\u2019t added a video';
      }
      if (body) {
        body.textContent = isPhoto
          ? 'The framed print is one of the things that stays after the '
            + 'chocolates are gone. You can go back and add one, or carry '
            + 'on without it.'
          : 'The card in the envelope opens your video with a tap. Without '
            + 'one, the card stays out of the box. You can go back and add '
            + 'it, or carry on without it.';
      }
      if (back) {
        back.textContent = isPhoto ? 'Add a photograph' : 'Add a video';
        back.dataset.askBack = kind;
      }

      this.openAsk(box);
    }

    openAsk(box) {
      if (box.open) return;
      if (typeof box.showModal === 'function') {
        try { box.showModal(); return; } catch (e) { /* fall through */ }
      }
      box.setAttribute('open', '');
    }

    closeAsk() {
      var box = this.querySelector('[data-ask]');
      if (!box || !box.open) return;
      if (typeof box.close === 'function') {
        try { box.close(); return; } catch (e) { /* fall through */ }
      }
      box.removeAttribute('open');
    }

    /* Straight to the step they skipped, rather than back one at a time. */
    jumpTo(key) {
      var at = -1;
      this.steps.forEach(function (step, i) {
        if (step.dataset.key === key) at = i;
      });
      if (at < 0) return;
      this.index = at;
      this.clearError();
      this.render();
    }

    /* Options are chosen one screen at a time; the variant is whichever
       combination they add up to. */
    resolveVariant() {
      var picked = this.state.options;
      var complete = picked.length > 0 && picked.every(function (value) { return !!value; });

      if (!complete) { this.state.variant = ''; return; }

      var match = this.variants.filter(function (variant) {
        return picked.every(function (value, i) { return variant.options[i] === value; });
      })[0];

      this.state.variant = match ? String(match.id) : '';
    }

    optionLabel(index) {
      var step = this.optionSteps[index];
      return step ? (step.dataset.optionName || 'option') : 'option';
    }

    validate(key) {
      var s = this.state;

      if (key.indexOf('option-') === 0) {
        var index = parseInt(key.slice(7), 10);
        if (!s.options[index]) return 'Choose a ' + this.optionLabel(index).toLowerCase() + '.';
        var isLast = index === this.optionSteps.length - 1;
        if (isLast && !s.variant) return 'That combination is not available — try another.';
        return null;
      }

      if (key === 'who' && !s.name.trim()) return 'Their name, so we know who this is for.';
      if (key === 'chocolates' && this.total() !== this.min) {
        var left = this.min - this.total();
        return left > 0 ? 'Choose ' + left + ' more.' : 'Remove ' + (-left) + '.';
      }
      if (key === 'message') {
        if (s.message.trim().length < 10) return 'A few words at least — this is the part they keep.';
        if (!s.sender.trim()) return 'Add your name so we know how to sign it.';
      }
      /* Nobody leaves a step with a file still climbing up a phone
         connection — the order would be placed without it. */
      if (key === 'photo' && this.busy.photo) {
        return 'Your photo is still uploading. One moment.';
      }
      if (key === 'video' && this.busy.video) {
        return 'Your video is still uploading. One moment.';
      }
      if (key === 'video' && !s.photo && !s.video) return 'Add a photo or a video, at least one of the two.';
      if (key === 'arrival') {
        if (!s.arrival) return 'Pick a date it should arrive by.';
        if (!s.reveal) return 'Let us know whether it should be a surprise.';
        if (s.reveal === 'Let them know' && !/.+@.+\..+/.test(s.recipientEmail)) {
          return 'We need their email to tell them it is coming.';
        }
      }
      return null;
    }

    showError(text) {
      var el = this.querySelector('[data-error]');
      if (!el) return;
      el.textContent = text;
      el.hidden = false;
    }

    clearError() {
      var el = this.querySelector('[data-error]');
      if (el) el.hidden = true;
    }

    /* ---------- rendering ---------- */

    total() {
      return this.state.counts.reduce(function (a, b) { return a + b; }, 0);
    }

    render() {
      var self = this;
      this.steps.forEach(function (step, i) { step.hidden = i !== self.index; });

      var last = this.index === this.steps.length - 1;
      this.querySelector('[data-back]').hidden = this.index === 0;
      this.querySelector('[data-next]').hidden = last;

      var bar = this.querySelector('[data-bar]');
      if (bar) bar.style.width = ((this.index + 1) / this.steps.length * 100).toFixed(1) + '%';
      var num = this.querySelector('[data-stepnum]');
      if (num) num.textContent = String(this.index + 1);
      var totalEl = this.querySelector('[data-steptotal]');
      if (totalEl) totalEl.textContent = String(this.steps.length);

      this.renderChocolates();
      this.updateNames();
      this.toggleRecipientEmail();
      this.measureButtons();
      this.collapseHeader(this.index > 0);
      this.updateStage();
      if (last) this.renderSummary();
    }

    updateNames() {
      var name = this.state.name.trim();
      this.querySelectorAll('[data-name-slot]').forEach(function (el) {
        el.textContent = name || el.dataset.nameSlot || 'them';
      });
    }

    toggleRecipientEmail() {
      var wrap = this.querySelector('[data-recipient-email]');
      if (wrap) wrap.hidden = this.state.reveal !== 'Let them know';
    }

    renderChocolates() {
      var self = this;
      var total = this.total();

      this.cards.forEach(function (card, i) {
        var n = self.state.counts[i];
        card.querySelector('[data-qty]').textContent = String(n);
        card.classList.toggle('is-picked', n > 0);
        var badge = card.querySelector('[data-badge]');
        badge.textContent = String(n);
        badge.hidden = n === 0;
        card.querySelector('[data-step-choc="-1"]').disabled = n === 0;
        card.querySelector('[data-step-choc="1"]').disabled = total >= self.max;
      });

      var count = this.querySelector('[data-count]');
      if (count) count.textContent = String(total);
    }

    summaryChocolates() {
      var self = this;
      var parts = [];
      this.cards.forEach(function (card, i) {
        if (self.state.counts[i] > 0) {
          parts.push(self.state.counts[i] + ' × ' + card.dataset.name);
        }
      });
      return parts.join(', ');
    }

    renderSummary() {
      var self = this;
      var s = this.state;

      var rows = [['For', s.name + (s.occasion ? ' · ' + s.occasion : '')]];

      this.optionSteps.forEach(function (step, i) {
        rows.push([step.dataset.optionName || 'Option', s.options[i] || '']);
      });

      rows = rows.concat([
        ['Chocolates', this.summaryChocolates()],
        ['Letter', s.message.length > 90 ? s.message.slice(0, 90).trim() + '…' : s.message],
        ['Signed', s.sender],
        ['Photo', s.photo ? 'Added' : 'We’ll print your letter for the frame'],
        ['Video', s.video ? 'Added' : 'No video — the card stays out'],
        ['Arrives by', this.prettyDate(s.arrival)],
        ['Reveal', s.reveal]
      ]);

      var dl = this.querySelector('[data-summary]');
      if (!dl) return;
      dl.innerHTML = '';
      rows.forEach(function (row) {
        if (!row[1]) return;
        var dt = document.createElement('dt');
        dt.textContent = row[0];
        var dd = document.createElement('dd');
        dd.textContent = row[1];
        dl.appendChild(dt);
        dl.appendChild(dd);
      });
    }

    /* ---------- the image stage ---------- */

    /* The stage is authored inside this component, then moved into the
       theme's gallery so it sits in the left column and inherits its
       sticky behaviour. The theme's own slideshow is hidden by CSS. */
    mountStage() {
      this.stage = this.querySelector('[data-stage]');
      if (!this.stage) return;

      this.stageImg = this.stage.querySelector('[data-stage-img]');
      this.stageThumbs = this.stage.querySelector('[data-stage-thumbs]');
      this.stageNote = this.stage.querySelector('[data-stage-note]');

      this.headerBlocks = this.findHeaderBlocks();

      var gallery = document.querySelector(this.dataset.gallery || 'media-gallery');
      if (gallery) {
        /* The theme's sticky script keeps repositioning this by hand,
           which fights our own. Take the class off and it stops. */
        gallery.classList.remove('sticky-content');
        gallery.classList.add('gb-has-stage');
        gallery.appendChild(this.stage);
      }

      var self = this;
      this.stageThumbs.addEventListener('click', function (event) {
        var thumb = event.target.closest('[data-thumb]');
        if (thumb) self.showImage(thumb.dataset.thumb);
      });

      this.buildStageControls();
    }

    /* Arrows on the picture itself, and a way to fill the screen with it.
       The thumbnails stay — they show what else there is — but nobody
       should have to scroll to a strip of 40px squares to see the next
       photograph, least of all on a phone. */
    buildStageControls() {
      var self = this;
      var frame = this.stage.querySelector('.gb__stage-frame');
      if (!frame) return;

      var arrow = function (direction, label, glyph) {
        var button = document.createElement('button');
        button.type = 'button';
        button.className = 'gb__stagenav gb__stagenav--' + direction;
        button.setAttribute('aria-label', label);
        button.innerHTML = glyph;
        button.hidden = true;
        button.addEventListener('click', function (event) {
          event.preventDefault();
          event.stopPropagation();
          self.stepImage(direction === 'prev' ? -1 : 1);
        });
        frame.appendChild(button);
        return button;
      };

      this.navPrev = arrow('prev', 'Previous image', '&lsaquo;');
      this.navNext = arrow('next', 'Next image', '&rsaquo;');

      var expand = document.createElement('button');
      expand.type = 'button';
      expand.className = 'gb__expand';
      expand.setAttribute('aria-label', 'View larger');
      expand.innerHTML = '<svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M9 3H3v6M15 21h6v-6M21 9V3h-6M3 15v6h6"/></svg>';
      expand.hidden = true;
      expand.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        self.openFull();
      });
      frame.appendChild(expand);
      this.expandButton = expand;

      this.stageFrame = frame;
      this.buildFullView();
      this.wireSwipe(frame, false);

      /* The picture is its own button. Anything with a job of its own —
         the arrows, the enlarge icon, the crop toggle — is skipped, and
         a drag is not a tap. */
      frame.addEventListener('click', function (event) {
        if (event.target.closest('button')) return;
        if (self.dragged) { self.dragged = false; return; }
        if (self.overlayShowing()) return;
        self.openFull();
      });
      this.refreshStageControls();
    }

    /* A finger, not an arrow. The arrows only appear under a cursor, and
       a phone has neither cursor nor hover — so on touch the picture is
       simply dragged sideways. The threshold and the axis test keep an
       ordinary vertical scroll from being read as a swipe. */
    wireSwipe(element, inFull) {
      var self = this;
      var startX = 0;
      var startY = 0;
      var tracking = false;

      element.addEventListener('touchstart', function (event) {
        if (event.touches.length !== 1) { tracking = false; return; }
        if (inFull && self.fullScroll && self.fullScroll.classList.contains('is-zoomed')) {
          tracking = false;
          return;
        }
        tracking = true;
        startX = event.touches[0].clientX;
        startY = event.touches[0].clientY;
      }, { passive: true });

      element.addEventListener('touchend', function (event) {
        if (!tracking) return;
        tracking = false;

        var touch = event.changedTouches[0];
        if (!touch) return;

        var dx = touch.clientX - startX;
        var dy = touch.clientY - startY;
        if (Math.abs(dx) > 8 || Math.abs(dy) > 8) self.dragged = true;
        if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy) * 1.5) return;

        self.stepImage(dx < 0 ? 1 : -1, inFull);
      }, { passive: true });
    }

    buildFullView() {
      var self = this;
      var dialog = document.createElement('dialog');
      dialog.className = 'gb__full';
      dialog.innerHTML =
        '<button type="button" class="gb__full-close" aria-label="Close">&times;</button>' +
        '<button type="button" class="gb__full-nav gb__full-nav--prev" aria-label="Previous image">&lsaquo;</button>' +
        '<button type="button" class="gb__full-nav gb__full-nav--next" aria-label="Next image">&rsaquo;</button>' +
        '<div class="gb__full-scroll"><img class="gb__full-img" alt=""></div>' +
        '<div class="gb__full-thumbs"></div>';

      dialog.addEventListener('click', function (event) {
        if (event.target.closest('.gb__full-close')) { self.closeFull(); return; }
        if (event.target.closest('.gb__full-nav--prev')) { self.stepImage(-1, true); return; }
        if (event.target.closest('.gb__full-nav--next')) { self.stepImage(1, true); return; }
        var thumb = event.target.closest('[data-full-thumb]');
        if (thumb) { self.setFullImage(thumb.dataset.fullThumb); return; }
        if (event.target.closest('.gb__full-thumbs')) return;
        /* Tapping the picture zooms; tapping the space around it closes. */
        if (event.target.closest('.gb__full-img')) { self.toggleFullZoom(); return; }
        if (event.target.closest('.gb__full-scroll')) self.closeFull();
      });

      document.body.appendChild(dialog);
      this.full = dialog;
      this.fullImg = dialog.querySelector('.gb__full-img');
      this.fullScroll = dialog.querySelector('.gb__full-scroll');
      this.fullPrev = dialog.querySelector('.gb__full-nav--prev');
      this.fullNext = dialog.querySelector('.gb__full-nav--next');
      this.fullThumbs = dialog.querySelector('.gb__full-thumbs');

      this.wireSwipe(this.fullScroll, true);

      dialog.addEventListener('keydown', function (event) {
        if (event.key === 'ArrowLeft') { event.preventDefault(); self.stepImage(-1, true); }
        if (event.key === 'ArrowRight') { event.preventDefault(); self.stepImage(1, true); }
      });
      dialog.addEventListener('close', function () {
        document.documentElement.classList.remove('gb-full-open');
      });
    }

    /* An overlay — their photograph in the frame, or the letter — is a
       composed preview, not a photograph of ours. Paging and enlarging
       it would show the bare backdrop underneath, so the controls step
       aside while one is up. */
    overlayShowing() {
      if (!this.stage) return false;
      /* A composed preview — the framed photo, the letter, the video card.
         Not a photograph of ours, and not something to enlarge. */
      if (this.customStage) return true;
      var mock = this.stage.querySelector('[data-mock]');
      var letter = this.stage.querySelector('[data-letter]');
      return !!((mock && !mock.hidden) || (letter && !letter.hidden));
    }

    refreshStageControls() {
      if (!this.navPrev) return;

      var list = this.stageList || [];
      var overlay = this.overlayShowing();
      var many = list.length > 1 && !overlay;

      this.navPrev.hidden = !many;
      this.navNext.hidden = !many;
      this.expandButton.hidden = overlay || !list.length;

      /* The cursor has to agree with the controls, or the picture invites
         a click it will not answer. */
      if (this.stageFrame) {
        this.stageFrame.style.cursor = overlay || !list.length ? '' : 'zoom-in';
      }

      if (this.fullPrev) {
        this.fullPrev.hidden = !many;
        this.fullNext.hidden = !many;
      }
    }

    stepImage(direction, inFull) {
      var list = this.stageList || [];
      if (list.length < 2) return;

      var at = list.indexOf(this.currentImage);
      if (at < 0) at = 0;
      var next = (at + direction + list.length) % list.length;

      this.showImage(list[next]);
      if (inFull) this.setFullImage(list[next]);
    }

    /* The full view keeps its own copy of the current image so the two
       stay in step whichever one is driving. */
    setFullImage(src) {
      if (!this.fullImg || !src) return;
      this.fullImg.src = src;
      this.zoomOut();
      this.showImage(src);

      this.fullThumbs.querySelectorAll('[data-full-thumb]').forEach(function (thumb) {
        thumb.classList.toggle('is-current', thumb.dataset.fullThumb === src);
      });
    }

    renderFullThumbs() {
      if (!this.fullThumbs) return;
      var list = this.stageList || [];

      this.fullThumbs.innerHTML = '';
      this.fullThumbs.hidden = list.length < 2;
      if (list.length < 2) return;

      list.forEach(function (src) {
        var button = document.createElement('button');
        button.type = 'button';
        button.className = 'gb__full-thumb';
        button.dataset.fullThumb = src;
        if (src === this.currentImage) button.classList.add('is-current');
        var img = document.createElement('img');
        img.src = src;
        img.alt = '';
        button.appendChild(img);
        this.fullThumbs.appendChild(button);
      }, this);
    }

    openFull() {
      if (!this.full || !this.currentImage) return;
      /* Last line of defence: whatever route asked for it, there is
         nothing here worth enlarging while a preview is composed. */
      if (this.overlayShowing()) return;
      this.fullImg.src = this.currentImage;
      this.zoomOut();
      this.renderFullThumbs();
      this.refreshStageControls();
      if (typeof this.full.showModal === 'function') this.full.showModal();
      else this.full.setAttribute('open', '');
      document.documentElement.classList.add('gb-full-open');
    }

    closeFull() {
      if (!this.full) return;
      if (typeof this.full.close === 'function') this.full.close();
      else this.full.removeAttribute('open');
      document.documentElement.classList.remove('gb-full-open');
    }

    /* Two states rather than free zoom: fit the screen, or twice that
       and pannable. Pinch still works on top of either. */
    toggleFullZoom() {
      if (!this.fullScroll) return;
      if (this.fullScroll.classList.contains('is-zoomed')) { this.zoomOut(); return; }
      this.fullScroll.classList.add('is-zoomed');
      this.fullScroll.scrollTop = (this.fullScroll.scrollHeight - this.fullScroll.clientHeight) / 2;
      this.fullScroll.scrollLeft = (this.fullScroll.scrollWidth - this.fullScroll.clientWidth) / 2;
    }

    zoomOut() {
      if (this.fullScroll) this.fullScroll.classList.remove('is-zoomed');
    }

    /* The little pictures on the buttons come from the same tagged groups
       as the stage does, so once the blue box is chosen the bedding
       choices show blue too. Liquid cannot do this: it renders once, and
       the first variant carrying a given bedding happens to be a red one,
       which is why every button was red whatever had been picked. */
    refreshChoices(step, index) {
      var self = this;
      var name = this.optionLabel(index);

      Array.from(step.querySelectorAll('[data-option]')).forEach(function (input) {
        var images = self.findSet(name, input.value, true);
        if (!images || !images.length) return;

        var label = input.closest('.gb__box');
        var img = label && label.querySelector('img');
        if (!img) return;

        var wanted = images[0];
        if (img.getAttribute('src') === wanted) return;

        /* Decoded first, so a button never blinks empty mid-change. */
        var preload = new Image();
        preload.onload = function () { img.src = wanted; };
        preload.onerror = function () { img.src = wanted; };
        preload.src = wanted;
      });
    }

    /* Which chocolate the stage should be showing. Not the first still in
       the box, but the one chosen most recently — so removing the third
       of three returns to the second, and removing the middle one of an
       out-of-order run returns to whatever was picked after it. */
    touch(index) {
      this.order = (this.order || []).filter(function (n) { return n !== index; });
      this.order.push(index);
    }

    drop(index) {
      this.order = (this.order || []).filter(function (n) { return n !== index; });
    }

    latest() {
      var order = this.order || [];
      for (var i = order.length - 1; i >= 0; i--) {
        if (this.state.counts[order[i]] > 0) return this.cards[order[i]];
      }
      return null;
    }

    /* The chosen chocolate leads, and the step's own pictures follow it in
       the thumbnails — so someone can look at the chocolate and then back
       at the whole selection without leaving the step. */
    showChocolate(card) {
      var section = this.findSet('chocolates', '', true) || [];

      if (!card) {
        this.showImages(section.length ? section : this.fallback);
        return;
      }

      var own = this.findExact('chocolates', card.dataset.name)
        || (card.dataset.image ? [card.dataset.image] : []);

      var list = own.concat(section.filter(function (src) {
        return own.indexOf(src) === -1;
      }));

      this.showImages(list.length ? list : this.fallback);
    }

    /* Exact step-and-value match only. Used for the chocolates, where
       falling back to a step-wide set would show the same pictures for
       every one of them. */
    findExact(step, value) {
      var wanted = String(step || '').toLowerCase();
      var wantedValue = String(value || '').toLowerCase();
      var self = this;
      var hit = this.best(this.imageSets.filter(function (set) {
        return String(set.step).toLowerCase() === wanted
          && String(set.value).toLowerCase() === wantedValue
          && set.images.length
          && self.inContext(set);
      }));
      return hit ? hit.images : null;
    }

    /* Everything the theme renders above the flow — title, price,
       description — found by climbing to the block that sits beside them. */
    findHeaderBlocks() {
      var node = this;
      while (node.parentElement && !node.parentElement.querySelector('h1')) {
        node = node.parentElement;
      }
      if (!node.parentElement) return [];

      this.blockNode = node;

      var blocks = [];
      var sibling = node.previousElementSibling;
      while (sibling) { blocks.push(sibling); sibling = sibling.previousElementSibling; }
      return blocks;
    }

    collapseHeader(collapsed) {
      if (!this.headerBlocks || !this.headerBlocks.length) return;

      /* Measure the space the header occupied once, while it is still
         there, and hold it open afterwards so the steps stay put
         instead of jumping up the page. */
      if (this._gap === undefined && this.blockNode) {
        var top = this.headerBlocks[this.headerBlocks.length - 1];
        var gap = this.blockNode.getBoundingClientRect().top - top.getBoundingClientRect().top;
        this._gap = gap > 0 ? Math.round(gap) : 0;
        this.style.setProperty('--gb-gap', this._gap + 'px');
      }

      this.classList.toggle('is-collapsed', collapsed);
      this.headerBlocks.forEach(function (block) {
        block.classList.toggle('gb-collapsed', collapsed);
      });
    }

    /* Matching goes from most specific to least: this step and this
       selection, then this step, then the default set, then the product
       image — so a half-filled admin still renders something sensible. */
    /* A group may be tagged with "when" — an earlier choice it belongs to.
       Once the red box is chosen, only the red bedding pictures are ever
       eligible. Groups with no tag stay eligible throughout, so tagging
       is opt-in and nothing has to be filled in twice. */
    inContext(set) {
      if (!set.when) return true;
      var wanted = String(set.when).toLowerCase();
      return (this.state.options || []).some(function (chosen) {
        return String(chosen || '').toLowerCase() === wanted;
      });
    }

    /* A tagged group beats an untagged one, so a general picture can sit
       behind the per-colour ones as a fallback. */
    best(list) {
      var tagged = list.filter(function (set) { return !!set.when; })[0];
      return tagged || list[0];
    }

    findSet(step, value, strict) {
      var self = this;
      var wanted = String(step || '').toLowerCase();
      var wantedValue = String(value || '').toLowerCase();

      var exact = this.best(this.imageSets.filter(function (set) {
        return String(set.step).toLowerCase() === wanted
          && String(set.value).toLowerCase() === wantedValue
          && set.images.length
          && self.inContext(set);
      }));
      if (exact) return exact.images;

      var stepOnly = this.best(this.imageSets.filter(function (set) {
        return String(set.step).toLowerCase() === wanted && !set.value
          && set.images.length && self.inContext(set);
      }));
      if (stepOnly) return stepOnly.images;

      if (strict) return null;

      var fallbackSet = this.imageSets.filter(function (set) {
        return String(set.step).toLowerCase() === 'default' && set.images.length;
      })[0];
      return fallbackSet ? fallbackSet.images : this.fallback;
    }

    updateStage() {
      if (!this.stage) return;
      var step = this.steps[this.index];
      if (!step) return;
      var key = step.dataset.key;

      /* Any step can carry a caption for under the image via data-note. */
      if (this.stageNote) {
        var note = step.dataset.note || '';
        this.stageNote.textContent = note;
        this.stageNote.hidden = !note;
      }

      if (key.indexOf('option-') === 0) {
        var i = parseInt(key.slice(7), 10);
        this.refreshChoices(step, i);
        this.showImages(this.findSet(this.optionLabel(i), this.state.options[i]));
        return;
      }

      if (key === 'message' && this.stage && this.stage.querySelector('[data-letter]')) {
        this.showLetter(this.state.message);
        return;
      }

      /* The empty frame shows from the moment they arrive; their photo
         drops into it when they upload. Same idea as the letter. */
      if (key === 'photo' && this.stage.querySelector('[data-mock]')) {
        this.showUserPhoto(this.photoPreview || this.state.photo);
        return;
      }

      if (key === 'photo' && this.state.photo) {
        this.showUserPhoto(this.photoPreview || this.state.photo);
        return;
      }

      /* Like the frame: the empty phone is there from the moment they
         reach the step, and their film drops into it when it uploads. */
      if (key === 'video' && this.stage.querySelector('[data-phone]')) {
        this.showUserVideo(this.videoPreview);
        return;
      }

      this.hideOverlays();
      this.showImages(this.findSet(key, ''));
    }

    /* Which step they are standing on — an upload should only redraw
       the stage when the stage is showing that step. */
    stepKey() {
      var step = this.steps && this.steps[this.index];
      return step ? step.dataset.key : '';
    }

    /* Their own film, playing in the phone that is held to the card —
       which is how the person receiving it will actually see it. */
    showUserVideo(src) {
      if (!this.stage) return;
      var phone = this.stage.querySelector('[data-phone]');
      if (!phone) return;

      this.customStage = true;
      this.stageThumbs.innerHTML = '';
      this.stageThumbs.hidden = true;

      var mock = this.stage.querySelector('[data-mock]');
      if (mock) mock.hidden = true;
      var letter = this.stage.querySelector('[data-letter]');
      if (letter) letter.hidden = true;

      var film = phone.querySelector('[data-phone-video]');
      var blank = phone.querySelector('[data-phone-blank]');

      if (film) {
        if (src) {
          if (film.getAttribute('src') !== src) {
            film.src = src;
            /* A file the browser cannot decode — an older .mov, say — is
               still a perfectly good video for the card; only the
               preview cannot show it. Say so instead of leaving a black
               rectangle that reads as a broken upload. */
            film.onerror = function () {
              film.hidden = true;
              if (blank) {
                blank.hidden = false;
                blank.firstElementChild.textContent =
                  'This one will not preview here, but it is on the card.';
              }
            };
            film.load();
          }
          film.hidden = false;

          /* A landscape film in an upright phone is two black bands and
             a stamp of picture. Turn the handset instead, the way anyone
             would with a real one. Read from the file rather than from
             the name, since neither tells the truth reliably. */
          var turn = function () {
            if (!film.videoWidth) return;
            phone.classList.toggle('is-wide', film.videoWidth > film.videoHeight);
          };
          film.onloadedmetadata = turn;
          if (film.readyState >= 1) turn();
        } else {
          film.pause();
          film.removeAttribute('src');
          film.load();
          film.hidden = true;
          phone.classList.remove('is-wide');
        }
      }

      if (blank) {
        blank.hidden = !!src;
        /* A reload throws away the browser's handle on their file, so
           the preview cannot come back \u2014 but the video itself is
           uploaded and on the order, and saying nothing would read as
           having lost it. */
        if (!src) {
          blank.firstElementChild.textContent = this.state.video
            ? 'Your video is saved. It plays when they tap the card.'
            : 'Your video plays here';
        }
      }

      phone.hidden = false;
      this.stageImg.style.visibility = 'hidden';
      this.refreshStageControls();
      this.runTap(phone, film);
    }

    /* The tap, on a loop.

       The five stages are driven from here rather than from a CSS
       animation so the film can be started at the exact moment the
       screen lights — a keyframe animation and a <video> have no way to
       agree on when that is, and they drift apart within a minute. */
    runTap(phone, film) {
      this.stopTap();

      var self = this;
      var styles = window.getComputedStyle(phone);
      function ms(name, fallback) {
        var raw = styles.getPropertyValue(name).trim();
        if (!raw) return fallback;
        var n = parseFloat(raw);
        if (isNaN(n)) return fallback;
        return raw.indexOf('ms') > -1 ? n : n * 1000;
      }

      var slide = ms('--gb-ph-slide', 900);
      var tap = ms('--gb-ph-tap', 600);
      var wake = ms('--gb-ph-wake', 500);
      var hold = ms('--gb-ph-hold', 5500);
      var rest = ms('--gb-ph-rest', 900);
      var settle = ms('--gb-ph-settle', 600);

      var still = window.matchMedia
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      function play() {
        if (!film || film.hidden) return;
        try { film.currentTime = 0; } catch (e) {}
        var going = film.play();
        if (going && going.catch) going.catch(function () {});
      }

      /* A film shorter than the hold would sit on its last frame — or
         loop a stub — so the cycle shortens to fit it instead. */
      function holdFor() {
        if (!film || film.hidden) return hold;
        var length = film.duration;
        if (!length || !isFinite(length)) return hold;
        return Math.min(hold, Math.max(1200, length * 1000));
      }

      if (still) {
        phone.classList.add('is-in', 'is-play');
        phone.classList.remove('is-tap');
        play();
        return;
      }

      function at(delay, fn) { self._tap.push(window.setTimeout(fn, delay)); }

      /* Every cycle begins on an empty phone: dark screen, no card. The
         two pauses are separate on purpose — rest is the quiet before
         anything happens, settle is the card lying against the phone
         for a moment before it registers. Collapsing them into one
         number always makes one of the two beats feel wrong. */
      function cycle() {
        self._tap = [];
        var span = holdFor();
        var t = rest;

        at(t, function () {
          phone.classList.add('is-in');
        });

        t += slide + settle;
        at(t, function () {
          phone.classList.add('is-tap');
        });

        /* Contact: the screen comes up and the card withdraws, so the
           film is never watched through a card lying across it. */
        t += tap;
        at(t, function () {
          phone.classList.remove('is-tap', 'is-in');
          phone.classList.add('is-play');
          play();
        });

        /* The film finishes on its own, and the screen sleeps before
           anything else moves — a card returning over the ending reads
           as an interruption. */
        t += wake + span;
        at(t, function () {
          phone.classList.remove('is-play');
          if (film) film.pause();
        });

        at(t + wake, cycle);
      }

      phone.classList.remove('is-in', 'is-tap', 'is-play');
      cycle();
    }

    stopTap() {
      (this._tap || []).forEach(function (id) { window.clearTimeout(id); });
      this._tap = [];
    }

    /* Their own photo: dropped into the frame mockup when one exists,
       otherwise shown whole rather than cropped to a square. */
    showUserPhoto(src) {
      if (!this.stage) return;
      /* Their own photograph, or the frame mock-up standing in for one.
         Either way the stage is no longer showing a picture of ours. */
      this.customStage = true;
      this.refreshStageControls();
      this.hidePhone();
      var mock = this.stage.querySelector('[data-mock]');

      this.stageThumbs.innerHTML = '';
      this.stageThumbs.hidden = true;

      var letter = this.stage.querySelector('[data-letter]');
      if (letter) letter.hidden = true;

      var cropButton = this.querySelector('[data-crop]');

      if (mock) {
        var slot = mock.querySelector('[data-mock-photo]');

        /* Turn the frame to suit the photograph, when there is a picture of
           it turned. Measured from the file itself rather than guessed
           from its name or the upload's metadata. */
        if (src && mock.dataset.hasWide) {
          var probe = new Image();
          probe.onload = function () {
            mock.classList.toggle('is-wide', probe.naturalWidth > probe.naturalHeight);
          };
          probe.src = src;
        } else if (!src) {
          mock.classList.remove('is-wide');
        }

        if (src) {
          slot.src = src;
          slot.hidden = false;
        } else {
          slot.removeAttribute('src');
          slot.hidden = true;
          mock.classList.remove('is-whole');
        }
        if (cropButton) {
          cropButton.hidden = !src;
          if (!src) cropButton.textContent = 'See the whole photo';
        }
        mock.hidden = false;
        this.stageImg.style.visibility = 'hidden';
        return;
      }

      if (!src) return;

      this.stageImg.classList.add('is-contain');
      this.showImage(src);
    }

    /* Their words, landing on the paper as they type. */
    showLetter(text) {
      this.customStage = true;
      this.refreshStageControls();
      if (!this.stage) return;
      this.hidePhone();
      var letter = this.stage.querySelector('[data-letter]');
      if (!letter) return;

      var target = letter.querySelector('[data-letter-text]');
      var written = (text || '').trim();
      target.textContent = written || 'Your words will appear here.';
      target.classList.toggle('is-placeholder', !written);

      var sign = letter.querySelector('[data-letter-sign]');
      if (sign) {
        sign.textContent = this.state.sender.trim();
        this.fitSign(sign);
      }

      /* The card only goes in the envelope when there is a video, and the
         printed letter says so — so show whichever version they'll get. */
      var paper = letter.querySelector('[data-letter-img]');
      if (paper) {
        var wanted = (this.state.video && paper.dataset.card)
          ? paper.dataset.card
          : paper.dataset.plain;
        if (wanted && paper.getAttribute('src') !== wanted) paper.src = wanted;
      }

      var mock = this.stage.querySelector('[data-mock]');
      if (mock) mock.hidden = true;
      letter.hidden = false;
      this.stageImg.style.visibility = 'hidden';
      this.stageThumbs.innerHTML = '';
      this.stageThumbs.hidden = true;

      this.fitLetter(target);
    }

    /* Shrink until it fits rather than letting a long letter spill off
       the paper. Throttled to one pass per frame while they type. */
    fitLetter(target) {
      if (this._fitting) return;
      this._fitting = true;
      var self = this;

      window.requestAnimationFrame(function () {
        var styles = window.getComputedStyle(target);
        var unit = relativeUnit(self.stage);

        /* The range is a percentage of the stage's width. Written in
           container units it stays a percentage after the fit, so the
           type follows the picture when the window changes size — a
           pixel value measured once is frozen at whatever width the
           step happened to be opened at, which is why the letter read
           correctly on a phone and far too small on a desktop. */
        var max = parseFloat(styles.getPropertyValue('--gb-lt-max')) || 2;
        var min = parseFloat(styles.getPropertyValue('--gb-lt-min')) || 1.5;
        if (unit.px) {
          max = unit.width * max / 100;
          min = unit.width * min / 100;
        }

        var step = Math.max(unit.px ? 0.25 : 0.02, (max - min) / 40);
        var size = max;
        target.style.fontSize = size + unit.name;
        while (size > min && target.scrollHeight > target.clientHeight) {
          size -= step;
          target.style.fontSize = size + unit.name;
        }
        self._fitting = false;
      });
    }

    /* Cover is the truth — that is how it prints. Contain is the check:
       "is anything important falling outside the frame?" */
    toggleCrop() {
      var mock = this.stage && this.stage.querySelector('[data-mock]');
      var button = this.querySelector('[data-crop]');
      if (!mock || !button) return;

      var whole = mock.classList.toggle('is-whole');
      button.textContent = whole ? 'See how it will print' : 'See the whole photo';

      var slot = mock.querySelector('[data-mock-photo]');
      var aperture = mock.querySelector('.gb__mock-aperture');
      if (!slot || !aperture) return;

      if (!whole) {
        slot.style.width = '';
        slot.style.height = '';
        return;
      }

      /* Work out the size the photo is already rendering at inside the
         opening, and pin it there. Left to itself the browser would use
         the file's natural size, which is thousands of pixels wide. */
      var box = aperture.getBoundingClientRect();
      var naturalW = slot.naturalWidth || box.width;
      var naturalH = slot.naturalHeight || box.height;
      var scale = Math.max(box.width / naturalW, box.height / naturalH);

      slot.style.width = (naturalW * scale) + 'px';
      slot.style.height = (naturalH * scale) + 'px';
    }

    /* A signature is one line, so it shrinks by width rather than height.
       "John Mcadams, III, Your Highness" is the case this exists for. */
    fitSign(target) {
      var styles = window.getComputedStyle(target);
      var unit = relativeUnit(this.stage);
      var max = parseFloat(styles.getPropertyValue('--gb-ls-max')) || 2.2;
      var min = parseFloat(styles.getPropertyValue('--gb-ls-min')) || 1.5;
      if (unit.px) {
        max = unit.width * max / 100;
        min = unit.width * min / 100;
      }
      var step = Math.max(unit.px ? 0.25 : 0.02, (max - min) / 30);

      var size = max;
      target.style.fontSize = size + unit.name;
      while (size > min && target.scrollWidth > target.clientWidth) {
        size -= step;
        target.style.fontSize = size + unit.name;
      }
    }

    /* Leaving the video step: stop the film rather than let it play on
       behind a picture of chocolates. */
    hidePhone() {
      this.stopTap();
      if (!this.stage) return;
      var phone = this.stage.querySelector('[data-phone]');
      if (!phone) return;
      var film = phone.querySelector('[data-phone-video]');
      if (film && !film.paused) film.pause();
      phone.classList.remove('is-in', 'is-tap', 'is-play');
      phone.hidden = true;
    }

    hideOverlays() {
      if (!this.stage) return;
      this.hidePhone();
      var mock = this.stage.querySelector('[data-mock]');
      if (mock) mock.hidden = true;
      var letter = this.stage.querySelector('[data-letter]');
      if (letter) letter.hidden = true;
      this.stageImg.style.visibility = '';
      this.stageImg.classList.remove('is-contain');
      this.refreshStageControls();
    }

    showImages(list) {
      if (!this.stage || !list || !list.length) return;
      this.stageList = list;
      this.customStage = false;
      this.showImage(list[0]);
      this.currentImage = list[0];
      this.refreshStageControls();

      this.stageThumbs.innerHTML = '';
      this.stageThumbs.hidden = list.length < 2;
      if (list.length < 2) return;

      list.forEach(function (src) {
        var button = document.createElement('button');
        button.type = 'button';
        button.className = 'gb__thumb';
        button.dataset.thumb = src;
        var img = document.createElement('img');
        img.src = src;
        img.alt = '';
        img.loading = 'lazy';
        button.appendChild(img);
        this.stageThumbs.appendChild(button);
      }, this);
    }

    /* Swap instantly. The new image is decoded first, so there is no
       blank frame in between and no fade to dip through. */
    showImage(src) {
      if (!this.stageImg || !src || this.stageImg.src === src) return;
      var img = this.stageImg;

      var preload = new Image();
      preload.onload = function () { img.src = src; };
      preload.onerror = function () { img.src = src; };
      preload.src = src;

      this.currentImage = src;

      this.stageThumbs.querySelectorAll('[data-thumb]').forEach(function (thumb) {
        thumb.classList.toggle('is-current', thumb.dataset.thumb === src);
      });

      this.refreshStageControls();
    }

    emitImage(src) {
      if (!src) return;
      this.showImages([src]);
      this.dispatchEvent(new CustomEvent('gb:image', {
        bubbles: true,
        detail: { src: src, step: this.steps[this.index].dataset.key }
      }));
    }

    /* ---------- submit ---------- */

    submit() {
      var self = this;

      /* Re-check every step, not a hardcoded list — the option steps come
         from the product, so their number isn't known in advance. */
      for (var i = 0; i < this.steps.length; i++) {
        var key = this.steps[i].dataset.key;
        if (key === 'review') continue;
        var problem = this.validate(key);
        if (problem) {
          this.index = i;
          this.render();
          this.showError(problem);
          return;
        }
      }

      /* The content agreement. Nothing reaches the order until the
         customer has confirmed the material is theirs to send. */
      var terms = this.querySelector('[data-terms]');
      if (terms && !terms.checked) {
        this.showError('Please confirm the content rules before adding to cart.');
        terms.focus();
        return;
      }

      var form = this.closest('form');
      if (!form) return;

      var variantInput = form.querySelector('input[name="id"]');
      if (variantInput) variantInput.value = this.state.variant;

      var plan = this.plan();

      var values = {
        name: this.state.name,
        occasion: this.state.occasion,
        chocolates: this.summaryChocolates(),
        message: this.state.message,
        sender: this.state.sender,
        /* What the customer sees. The links themselves go into the
           underscore-prefixed fields below, which Shopify shows only to us. */
        photo: this.state.photo ? 'Uploaded' : '',
        video: this.state.video ? 'Uploaded' : '',
        photoUrl: this.state.photo,
        /* The id for the notebook, and the same id already built into
           the link that gets written to the card. */
        videoId: this.state.video,
        videoUrl: this.state.video
          ? 'https://vellucio.com/pages/watch#' + this.state.video
          : '',
        arrival: this.prettyDate(this.state.arrival),
        arrivalIso: this.state.arrival,
        shipBy: plan ? this.iso(plan.ship) : '',
        makeFrom: plan ? this.iso(plan.make) : '',
        reveal: this.state.reveal,
        recipientEmail: this.state.reveal === 'Let them know' ? this.state.recipientEmail : '',
        selection: JSON.stringify(this.selectionMap()),
        terms: 'Agreed ' + new Date().toISOString()
      };

      this.querySelectorAll('[data-out]').forEach(function (input) {
        var value = values[input.dataset.out] || '';
        input.value = value;
        input.disabled = value === '';
      });

      var button = this.querySelector('[data-submit]');
      if (button) { button.disabled = true; button.textContent = 'Adding…'; }

      /* We add to the cart ourselves rather than handing the form to the
         theme, so we know when it actually succeeded — and can say so. */
      fetch('/cart/add.js', {
        method: 'POST',
        body: new FormData(form),
        headers: { 'Accept': 'application/json' }
      })
        .then(function (response) {
          if (!response.ok) throw new Error('add failed');
          return response.json();
        })
        .then(function () {
          try { window.localStorage.removeItem(self.storageKey); } catch (e) { /* ignore */ }
          if (button) {
            button.textContent = 'Added to cart';
            button.classList.add('is-added');
          }
          setTimeout(function () { window.location.href = '/cart'; }, 1000);
        })
        .catch(function () {
          if (button) { button.disabled = false; button.textContent = 'Add to cart'; }
          self.showError('That did not go through. Try again?');
        });
    }

    selectionMap() {
      var self = this;
      var map = {};
      this.cards.forEach(function (card, i) {
        if (self.state.counts[i] > 0) map[card.dataset.name] = self.state.counts[i];
      });
      return map;
    }
  }

  customElements.define('gift-builder', GiftBuilder);
})();
